import { mergeHistory, mergeBookmarks, mergeCollections, normalizeHistoryPayload } from "./syncMerge";
import { uuid } from "./syncState";

const cardLM = (c) => c?.lastModified ?? c?.addedAt ?? 0;

// Own-key lookup for name-keyed maps: a collection may be named "constructor" or
// "toString", which a plain obj[name] resolves to an Object.prototype member.
const own = (obj, key) =>
  obj != null && Object.prototype.hasOwnProperty.call(obj, key) ? obj[key] : undefined;

// Local flashcard state keyed by collection id: id → { name, cards }.
const collectionsById = ({ collections, idMap }) => {
  const out = new Map();
  for (const [name, cards] of Object.entries(collections || {})) {
    const id = own(idMap, name);
    if (id) out.set(id, { name, cards });
  }
  return out;
};

const flashcardStateChanged = (a, b) =>
  ["collections", "idMap", "fcTombstones", "colTombstones", "collectionMeta"].some(
    (k) => a[k] !== b[k] && JSON.stringify(a[k] || {}) !== JSON.stringify(b[k] || {})
  );

const unionCards = (a, b) => {
  const byWord = new Map((a || []).map((c) => [c.wordID, c]));
  for (const c of b || []) {
    const prev = byWord.get(c.wordID);
    if (!prev || cardLM(c) > cardLM(prev)) byWord.set(c.wordID, c);
  }
  return [...byWord.values()];
};

const maxTombstones = (a, b) => {
  const out = { ...(a || {}) };
  for (const [wordID, ts] of Object.entries(b || {})) if (!(own(out, wordID) >= ts)) out[wordID] = ts;
  return out;
};

// The merge is built from the snapshot read at sync start, so a change the user made
// while the sync was in flight exists only in the live state. Carry each one onto the
// merge result by collection id, not name: the same sync may rename, delete or merge
// the collection the user was looking at. Merging the returned local side once more
// against what was published then keeps the change (B1).
//   • A change follows its collection into the same-named survivor it was merged into.
//   • A collection keeps its merged name (a peer's rename survives a card edit) unless
//     the user renamed or created it mid-sync.
//   • An unchanged collection whose name the user took mid-sync is left out, to be read
//     back from Drive next round: merging the two now would leave the renamed
//     collection's Drive file under its old name, to come back as a copy.
const rebaseMidSyncChanges = (snap, live, merged) => {
  const bucketOf = (state, id) => own(state.fcTombstones, id) || {};
  const lmOf = (state, id) => own(state.collectionMeta, id)?.lastModified ?? 0;
  const snapById = collectionsById(snap);
  const liveById = collectionsById(live);
  const changes = [];
  for (const [id, cur] of liveById) {
    const was = snapById.get(id);
    if (
      was &&
      was.name === cur.name &&
      JSON.stringify(was.cards) === JSON.stringify(cur.cards) &&
      JSON.stringify(bucketOf(snap, id)) === JSON.stringify(bucketOf(live, id)) &&
      lmOf(snap, id) === lmOf(live, id)
    )
      continue;
    changes.push({
      id: own(merged.mergedInto, id) ?? id,
      claim: !was || was.name !== cur.name, // renamed or created mid-sync
      name: cur.name,
      cards: cur.cards,
      deleted: bucketOf(live, id),
      lastModified: lmOf(live, id),
    });
  }

  const idOf = new Map(Object.entries(merged.collectionIdMap)); // name → id
  const nameOf = new Map([...idOf].map(([name, id]) => [id, name]));
  const cardsOf = new Map([...idOf].map(([name, id]) => [id, own(merged.collections, name)]));
  const fcTombstones = { ...merged.flashcardTombstones };
  const collectionTombstones = { ...merged.collectionTombstones };
  const collectionMeta = { ...merged.collectionMeta };
  const touched = new Set(changes.map((c) => c.id));
  const placed = new Set();
  const leftOut = new Set();
  const unname = (id) => {
    if (nameOf.has(id)) idOf.delete(nameOf.get(id));
    nameOf.delete(id);
  };

  // Deleted mid-sync: the fold weighs the new tombstone against the merged collection.
  for (const [id, ts] of Object.entries(live.colTombstones || {})) {
    if (ts <= (own(snap.colTombstones, id) ?? 0)) continue;
    collectionTombstones[id] = Math.max(own(collectionTombstones, id) ?? 0, ts);
    if (!liveById.has(id)) unname(id);
  }

  // Renames and creations go first: they claim their names.
  changes.sort((a, b) => b.claim - a.claim);
  for (const c of changes) {
    let id = c.id;
    const name = c.claim ? c.name : nameOf.get(id) ?? c.name;
    const holder = idOf.get(name);
    if (holder !== undefined && holder !== id) {
      if (!touched.has(holder)) {
        unname(holder);
        leftOut.add(holder);
      } else if (c.claim && !placed.has(holder)) {
        unname(holder); // its own change re-places it under its live name
      } else {
        unname(id); // two changed collections want one name: merge this one into the other
        leftOut.add(id);
        id = holder;
      }
    }
    if (nameOf.get(id) !== name) {
      unname(id);
      idOf.set(name, id);
      nameOf.set(id, name);
    }
    cardsOf.set(id, placed.has(id) ? unionCards(cardsOf.get(id), c.cards) : c.cards);
    placed.add(id);
    fcTombstones[id] = maxTombstones(own(fcTombstones, id), c.deleted);
    collectionMeta[id] = {
      lastModified: Math.max(own(collectionMeta, id)?.lastModified ?? 0, c.lastModified),
    };
  }

  return {
    collections: Object.fromEntries([...idOf].map(([name, id]) => [name, cardsOf.get(id)])),
    idMap: Object.fromEntries(idOf),
    fcTombstones,
    collectionTombstones,
    collectionMeta,
    touched,
    leftOut,
  };
};

// ── Canonicalisation (stable, order-insensitive compares to avoid no-op writes) ─
const sortCards = (cards) =>
  [...(cards || [])].sort((a, b) => (a.wordID < b.wordID ? -1 : a.wordID > b.wordID ? 1 : 0));

const canonCards = (cards) =>
  sortCards(cards).map((c) => ({
    word: c.word,
    wordID: c.wordID,
    root: c.root,
    definition: c.definition,
    addedAt: c.addedAt ?? 0,
    lastModified: cardLM(c),
  }));

const canonDeleted = (d) =>
  Object.fromEntries(Object.entries(d || {}).sort(([a], [b]) => (a < b ? -1 : 1)));

const sameCollection = (cardsA, deletedA, nameA, cardsB, deletedB, nameB) =>
  nameA === nameB &&
  JSON.stringify(canonCards(cardsA)) === JSON.stringify(canonCards(cardsB)) &&
  JSON.stringify(canonDeleted(deletedA)) === JSON.stringify(canonDeleted(deletedB));

// ── Payload builders ────────────────────────────────────────────────────────────
const buildBookmarksPayload = ({ items, meta, tombstones }) => ({
  schemaVersion: 2,
  items: Object.fromEntries(
    Object.entries(items || {})
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .map(([word, def]) => [
        word,
        { def, addedAt: meta?.[word]?.addedAt ?? 0, lastModified: meta?.[word]?.lastModified ?? 0 },
      ])
  ),
  deleted: canonDeleted(tombstones),
});

// Normalise any remote bookmarks content (v1 or v2) to the canonical payload shape.
const normalizeRemoteBookmarks = (content) => {
  if (!content) return null;
  if (content.schemaVersion === 2) {
    const items = {};
    const meta = {};
    for (const [w, v] of Object.entries(content.items || {})) {
      items[w] = v.def;
      meta[w] = { addedAt: v.addedAt ?? 0, lastModified: v.lastModified ?? 0 };
    }
    return buildBookmarksPayload({ items, meta, tombstones: content.deleted || {} });
  }
  // v1 plain { word: defs[] }
  const items = {};
  const meta = {};
  for (const [w, def] of Object.entries(content)) {
    items[w] = def;
    meta[w] = { addedAt: 0, lastModified: 0 };
  }
  return buildBookmarksPayload({ items, meta, tombstones: {} });
};

// ── History payload (v2: entries + tombstones) ───────────────────────────────
const buildHistoryPayload = ({ entries, tombstones }) => ({
  schemaVersion: 2,
  entries: [...(entries || [])].sort(
    (a, b) => b.timestamp - a.timestamp || (a.word < b.word ? -1 : a.word > b.word ? 1 : 0)
  ),
  deleted: canonDeleted(tombstones),
});

// Canonicalise remote history content (v1 array or v2 payload) to the payload shape.
const normalizeRemoteHistory = (content) => {
  const { entries, deleted } = normalizeHistoryPayload(content);
  return buildHistoryPayload({ entries, tombstones: deleted });
};

const mergeHistoryPayloads = (contents) => {
  let acc = { entries: [], tombstones: {} };
  for (const c of contents) {
    if (!c) continue;
    acc = mergeHistory(acc.entries, c, acc.tombstones);
  }
  return buildHistoryPayload(acc);
};

const buildCollectionPayload = (id, name, cards, lastModified, deleted) => ({
  id,
  name,
  cards: cards || [],
  lastModified: lastModified || 0,
  deleted: deleted || {},
  schemaVersion: 2,
});

// Reconcile N duplicate per-collection payloads into one (LWW per card + union deletes).
const mergeCollectionPayloads = (payloads) => {
  const cardMap = new Map();
  const deleted = {};
  let name;
  let lastModified = 0;
  let id;
  for (const p of payloads) {
    if (!p) continue;
    id = id || p.id;
    if ((p.lastModified ?? 0) >= lastModified) {
      lastModified = p.lastModified ?? 0;
      name = p.name ?? name;
    }
    for (const c of p.cards || []) {
      const prev = cardMap.get(c.wordID);
      if (!prev || cardLM(c) >= cardLM(prev)) cardMap.set(c.wordID, c);
    }
    for (const [wid, ts] of Object.entries(p.deleted || {})) {
      if (!deleted[wid] || ts > deleted[wid]) deleted[wid] = ts;
    }
  }
  for (const [wid, ts] of Object.entries(deleted)) {
    const c = cardMap.get(wid);
    if (c && ts >= cardLM(c)) cardMap.delete(wid);
  }
  return buildCollectionPayload(id, name, [...cardMap.values()], lastModified, deleted);
};

const mergeBookmarkPayloads = (contents) => {
  let acc = { items: {}, meta: {}, tombstones: {} };
  for (const c of contents) {
    if (!c) continue;
    acc = mergeBookmarks(acc.items, acc.meta, acc.tombstones, c);
  }
  return buildBookmarksPayload(acc);
};

// Reconcile duplicate LEGACY flashcards.json files (the v1 create race). Each is a
// raw { name: cards[] } map. Union by collection name, and within a name union cards
// by wordID (LWW). Picking one and deleting the rest (the old behaviour) silently
// destroyed whole collections that existed only in a duplicate.
const mergeV1FlashcardMaps = (contents) => {
  const out = new Map();
  let any = false;
  for (const c of contents) {
    if (!c || typeof c !== "object" || Array.isArray(c)) continue;
    any = true;
    for (const [name, cards] of Object.entries(c)) {
      const map = new Map((out.get(name) || []).map((k) => [k.wordID, k]));
      for (const card of cards || []) {
        const prev = map.get(card.wordID);
        if (!prev || cardLM(card) >= cardLM(prev)) map.set(card.wordID, card);
      }
      out.set(name, [...map.values()]);
    }
  }
  return any ? Object.fromEntries(out) : null;
};

const mergeIndexPayloads = (contents) => {
  const collections = new Map();
  const deleted = {};
  for (const c of contents) {
    if (!c || !c.schemaVersion) continue;
    for (const e of c.collections || []) {
      const prev = collections.get(e.id);
      if (!prev || (e.lastModified ?? 0) >= (prev.lastModified ?? 0)) collections.set(e.id, e);
    }
    for (const [id, ts] of Object.entries(c.deleted || {})) {
      if (!deleted[id] || ts > deleted[id]) deleted[id] = ts;
    }
  }
  return { schemaVersion: 2, collections: [...collections.values()], deleted };
};

/**
 * The sync engine. Pure orchestration over an injected `drive` adapter (the
 * contract documented in googleDrive.js) and a `store` (syncState.js). Every
 * public operation is a full read-merge-write-back, so devices converge to the
 * same state regardless of interleaving (state-based CRDT semantics).
 *
 * App data (collections / bookmarks / history) is passed IN and returned OUT —
 * the engine never owns it; it persists only sync metadata via `store`.
 */
export const createSyncEngine = ({ drive, store, filePrefix = "", mintId = uuid }) => {
  // Optional filename namespace. Default "" ⇒ byte-identical to the real files
  // (production untouched). The integration harness sets a prefix so its files
  // (e.g. itest_<run>_collections_index.json) never collide with real data.
  const P = filePrefix || "";
  const INDEX_FILE = `${P}collections_index.json`;
  const LEGACY_FLASHCARDS_FILE = `${P}flashcards.json`;
  const BOOKMARKS_FILE = `${P}bookmarks.json`;
  const HISTORY_FILE = `${P}history.json`;
  const COLLECTION_PREFIX = `${P}collection_`;
  const collectionFileName = (id) => `${COLLECTION_PREFIX}${id}.json`;
  const collectionIdFromName = (name) => {
    if (!name || !name.startsWith(COLLECTION_PREFIX)) return null;
    const m = /^(.+)\.json$/.exec(name.slice(COLLECTION_PREFIX.length));
    return m ? m[1] : null;
  };

  // One in-flight lock PER DOMAIN (bookmarks / history / flashcards). Concurrent
  // triggers within a domain serialize (so flashcard index/collection writes never
  // interleave), but the three independent files can sync in parallel.
  const chains = {};
  const runExclusive = (key, fn) => {
    const prev = chains[key] || Promise.resolve();
    const next = prev.then(fn, fn);
    chains[key] = next.catch(() => {});
    return next;
  };

  // Local state is read LAZILY inside the serialized section so a second queued
  // sync always sees the result of the first (never a stale snapshot).
  const resolveLocal = (a) => (typeof a === "function" ? a() : a);

  const oldestSurvivor = (files) =>
    [...files].sort(
      (a, b) => a.createdTime - b.createdTime || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
    )[0];

  // Resolve a fixed-name single file, collapsing duplicate same-named files
  // (the appDataFolder create-race) into one canonical file.
  const resolveFixed = async (name, mergeContents) => {
    const files = await drive.listByName(name);
    if (files.length === 0) {
      store.clearFileId(name);
      return { id: null, content: null, modifiedTime: 0 };
    }
    const survivor = oldestSurvivor(files);
    if (files.length === 1) {
      store.setFileId(name, survivor.id);
      const content = await drive.getContent(survivor.id);
      return { id: survivor.id, content, modifiedTime: survivor.modifiedTime };
    }
    const contents = await Promise.all(files.map((f) => drive.getContent(f.id)));
    const merged = mergeContents(contents);
    const written = await drive.update(survivor.id, JSON.stringify(merged));
    await Promise.all(
      files.filter((f) => f.id !== survivor.id).map((f) => drive.remove(f.id).catch(() => {}))
    );
    store.setFileId(name, survivor.id);
    return { id: survivor.id, content: merged, modifiedTime: written.modifiedTime };
  };

  // Write a fixed-name file, adopting an existing same-named file before creating
  // (so a stale/missing cache id doesn't spawn a duplicate).
  const writeFixed = async (name, contentObj) => {
    const body = JSON.stringify(contentObj);
    const cached = store.getFileId(name);
    if (cached) {
      try {
        return await drive.update(cached, body);
      } catch (e) {
        if (e?.status !== 404) throw e;
        store.clearFileId(name);
      }
    }
    const files = await drive.listByName(name);
    if (files.length) {
      const survivor = oldestSurvivor(files);
      store.setFileId(name, survivor.id);
      await Promise.all(
        files.filter((f) => f.id !== survivor.id).map((f) => drive.remove(f.id).catch(() => {}))
      );
      return await drive.update(survivor.id, body);
    }
    const created = await drive.create(name, body);
    store.setFileId(name, created.id);
    return created;
  };

  const writeCollection = (id, payload) => writeFixed(collectionFileName(id), payload);

  const deleteCollection = async (id) => {
    const name = collectionFileName(id);
    const files = await drive.listByName(name).catch(() => []);
    await Promise.all(files.map((f) => drive.remove(f.id).catch(() => {})));
    store.clearFileId(name);
  };

  // ── Bookmarks ───────────────────────────────────────────────────────────────
  const syncBookmarks = (getLocal) =>
    runExclusive("bookmarks", async () => {
      const local = resolveLocal(getLocal);
      const beforeItems = JSON.stringify(local.items || {});
      try {
        const { content, modifiedTime } = await resolveFixed(BOOKMARKS_FILE, mergeBookmarkPayloads);
        const merged = mergeBookmarks(local.items, local.meta, local.tombstones, content);
        const payload = buildBookmarksPayload(merged);
        const remoteNorm = normalizeRemoteBookmarks(content);

        if (JSON.stringify(payload) !== JSON.stringify(remoteNorm)) {
          const written = await writeFixed(BOOKMARKS_FILE, payload);
          store.setLastSyncedAt("bookmarks", written.modifiedTime);
        } else {
          store.setLastSyncedAt("bookmarks", modifiedTime);
        }
        // B1: if the user mutated bookmarks WHILE this sync was in flight, fold that
        // mutation (read from the store, where its items+meta are still consistent)
        // into the result so it is neither clobbered nor decoupled from its meta, and
        // keep pending so it re-syncs. Otherwise persist the plain merge.
        const afterLocal = resolveLocal(getLocal);
        const changedDuringSync = JSON.stringify(afterLocal.items || {}) !== beforeItems;
        let result = merged;
        if (changedDuringSync) {
          result = mergeBookmarks(
            afterLocal.items,
            afterLocal.meta,
            afterLocal.tombstones,
            buildBookmarksPayload(merged)
          );
          store.setBookmarksMeta(result.meta);
          store.setBookmarkTombstones(result.tombstones);
          store.setPendingSyncFlag("bookmarks", true);
        } else {
          store.setBookmarksMeta(merged.meta);
          store.setBookmarkTombstones(merged.tombstones);
          store.clearPendingSyncFlag("bookmarks");
        }
        return result; // { items, meta, tombstones }
      } catch (e) {
        store.setPendingSyncFlag("bookmarks", true);
        throw e;
      }
    });

  // ── History ─────────────────────────────────────────────────────────────────
  const syncHistory = (getLocal) =>
    runExclusive("history", async () => {
      const localArray = resolveLocal(getLocal);
      const beforeHist = JSON.stringify(localArray || []);
      try {
        const { content, modifiedTime } = await resolveFixed(HISTORY_FILE, mergeHistoryPayloads);
        const merged = mergeHistory(localArray, content, store.getHistoryTombstones());
        const payload = buildHistoryPayload(merged);
        const remoteNorm = normalizeRemoteHistory(content);
        if (JSON.stringify(payload) !== JSON.stringify(remoteNorm)) {
          const written = await writeFixed(HISTORY_FILE, payload);
          store.setLastSyncedAt("history", written.modifiedTime);
        } else {
          store.setLastSyncedAt("history", modifiedTime);
        }
        // B1: fold a mid-sync mutation into the result (consistent tombstones) and
        // keep pending; otherwise persist the plain merge.
        const afterHist = resolveLocal(getLocal);
        const changedDuringSync = JSON.stringify(afterHist || []) !== beforeHist;
        let result = merged;
        if (changedDuringSync) {
          result = mergeHistory(afterHist, buildHistoryPayload(merged), store.getHistoryTombstones());
          store.setHistoryTombstones(result.tombstones);
          store.setPendingSyncFlag("history", true);
        } else {
          store.setHistoryTombstones(merged.tombstones);
          store.clearPendingSyncFlag("history");
        }
        return result.entries; // callers persist/apply the entries array
      } catch (e) {
        store.setPendingSyncFlag("history", true);
        throw e;
      }
    });

  // ── Flashcards ──────────────────────────────────────────────────────────────
  // local = { collections:{name:cards[]}, idMap:{name:id}, fcTombstones, colTombstones, collectionMeta }
  // Always produces a COMPLETE merged local state. For speed it only fetches
  // collections that may differ (dirty / remote-changed / new / duplicated) and
  // only uploads collections whose content actually changed.
  const syncFlashcards = (getLocal) =>
    runExclusive("flashcards", async () => {
      const local = resolveLocal(getLocal);
      try {
      // 1. Resolve the index (reconcile duplicates); fall back to v1 legacy file.
      const indexRes = await resolveFixed(INDEX_FILE, mergeIndexPayloads);
      let remoteIndex = indexRes.content;
      if (!remoteIndex) {
        const legacy = await resolveFixed(LEGACY_FLASHCARDS_FILE, mergeV1FlashcardMaps);
        if (legacy.content) remoteIndex = legacy.content; // raw v1 map → mergeCollections handles it
      }

      const indexEntries = remoteIndex?.collections || [];
      const indexById = new Map(indexEntries.map((e) => [e.id, e]));

      // 2. List collection files; group by collection id (detect duplicates).
      const colFiles = await drive.listByPrefix(COLLECTION_PREFIX);
      const filesById = new Map();
      for (const f of colFiles) {
        const id = collectionIdFromName(f.name);
        if (!id) continue;
        if (!filesById.has(id)) filesById.set(id, []);
        filesById.get(id).push(f);
      }

      const dirty = new Set(store.getDirtyCollections());
      const syncedMT = store.getSyncedCollectionLM(); // stores last-seen SERVER modifiedTime per id
      const localIdMap = local.idMap || {};
      const localIds = new Set(Object.values(localIdMap).filter(Boolean));
      const colTombs = local.colTombstones || {};

      // Ids whose collection actually EXISTS in local card state (name is a live key
      // in local.collections). This is stricter than "id is in the id map": after a
      // JSON import (which replaces local collections but leaves the id map alone),
      // a dropped collection's id is still in the map but has no card state. Such an
      // id must NOT take the synth fast-path (which would fabricate an EMPTY payload
      // and erase the collection); it is treated as absent so its real Drive file is
      // fetched and merged back — never silently emptied.
      const localCardIds = new Set(
        Object.keys(local.collections || {})
          .map((n) => own(localIdMap, n))
          .filter(Boolean)
      );

      const unionIds = new Set([...indexById.keys(), ...filesById.keys(), ...localIds]);

      const fetchCollection = async (id) => {
        const files = filesById.get(id) || [];
        if (files.length === 0) return null;
        if (files.length === 1) return drive.getContent(files[0].id);
        // Duplicate per-collection files → reconcile.
        const contents = await Promise.all(files.map((f) => drive.getContent(f.id)));
        const merged = mergeCollectionPayloads(contents);
        const survivor = oldestSurvivor(files);
        // Persist the merged content to the survivor FIRST. Only if that write
        // succeeds may we delete the other duplicates. If it throws, we must NOT
        // remove anything — the merged content lives nowhere durable, and deleting
        // the duplicates would destroy content that existed only in a duplicate.
        // Let the error propagate: the caller records this collection as failed so
        // it is retried and never overwritten with stale local content this round.
        await drive.update(survivor.id, JSON.stringify(merged));
        await Promise.all(
          files.filter((f) => f.id !== survivor.id).map((f) => drive.remove(f.id).catch(() => {}))
        );
        return merged;
      };

      const synthLocalPayload = (id) => {
        const name = Object.keys(localIdMap).find((n) => localIdMap[n] === id);
        const cards = own(local.collections, name) || [];
        const metaLM = local.collectionMeta?.[id]?.lastModified ?? 0;
        const lm = cards.reduce((m, c) => Math.max(m, cardLM(c)), metaLM);
        return buildCollectionPayload(id, name, cards, lm, (local.fcTombstones || {})[id] || {});
      };

      // 3. Targeted fetch: only collections that may differ on either side.
      //    Change is detected via the file's monotonic SERVER modifiedTime
      //    (collision-free), never the content-derived lastModified.
      const remotePayloads = {};
      const fileMTById = {};
      const failedFetch = new Set(); // collections whose remote copy we could NOT read this round
      for (const id of unionIds) {
        const files = filesById.get(id) || [];
        const fileMT = files.reduce((m, f) => Math.max(m, f.modifiedTime), 0);
        fileMTById[id] = fileMT;
        const hasFile = files.length > 0;
        const hasDup = files.length > 1;
        // "Present" means we actually hold this collection's card state locally —
        // NOT merely that its id lingers in the id map (see localCardIds). An id
        // without local cards must be fetched, never synth-emptied.
        const localPresent = localCardIds.has(id);
        const remoteChanged = hasFile
          ? fileMT !== (syncedMT[id] ?? -1)
          : indexById.has(id); // index entry without a file ⇒ treat as changed
        const need = hasDup || dirty.has(id) || remoteChanged || !localPresent || !!colTombs[id];

        if (need && hasFile) {
          // A transient fetch failure for ONE collection must not abort the whole
          // sync — but it must ALSO not let us overwrite the (unread) remote with
          // stale local content. Record it as failed; step 5 leaves it untouched
          // and the watermark stays stale so it is refetched next round.
          try {
            const c = await fetchCollection(id);
            if (c) remotePayloads[id] = c;
            else failedFetch.add(id); // hasFile but content vanished/unreadable
          } catch (err) {
            console.warn("[sync] collection fetch failed, will retry:", id, err?.status);
            failedFetch.add(id);
          }
        } else if (hasFile && localPresent) {
          remotePayloads[id] = synthLocalPayload(id); // provably unchanged → no fetch, no upload
        }
        // index-only (no file) or brand-new local → no payload; merge handles it.
      }

      // 4. Full merge → complete new local state.
      const merged = mergeCollections(
        local.collections,
        localIdMap,
        local.fcTombstones,
        colTombs,
        remoteIndex,
        remotePayloads,
        local.collectionMeta,
        mintId
      );

      // 5. Upload only changed collections (record the SERVER modifiedTime).
      const newSyncedMT = { ...syncedMT };
      for (const [name, cards] of Object.entries(merged.collections)) {
        const id = merged.collectionIdMap[name];
        // A collection whose remote copy we failed to read this round must NOT be
        // overwritten with our (possibly stale) local content, and its watermark
        // must stay stale so it is refetched. Leave dirty set so it retries.
        if (failedFetch.has(id)) continue;
        const lm = merged.collectionMeta[id]?.lastModified ?? 0;
        const deleted = merged.flashcardTombstones[id] || {};
        const remote = remotePayloads[id];
        const upToDate =
          remote &&
          !dirty.has(id) &&
          sameCollection(cards, deleted, name, remote.cards, remote.deleted, remote.name);
        if (!upToDate) {
          const written = await writeCollection(id, buildCollectionPayload(id, name, cards, lm, deleted));
          newSyncedMT[id] = written.modifiedTime;
        } else {
          newSyncedMT[id] = fileMTById[id] || newSyncedMT[id] || 0;
        }
        store.clearCollectionDirty(id);
      }

      // Delete files for collections that were tombstoned / merged-away, and drop
      // their now-unreferenced per-card tombstone buckets.
      for (const id of merged.orphanCollectionIds) {
        if (failedFetch.has(id)) continue;
        await deleteCollection(id);
        delete newSyncedMT[id];
        delete merged.flashcardTombstones[id];
        store.clearCollectionDirty(id);
      }

      // 6. Read-merge-write the index (server-time watermark).
      const newIndex = {
        schemaVersion: 2,
        collections: Object.entries(merged.collections)
          .map(([name, cards]) => ({
            id: merged.collectionIdMap[name],
            name,
            lastModified: merged.collectionMeta[merged.collectionIdMap[name]]?.lastModified ?? 0,
          }))
          .sort((a, b) => (a.id < b.id ? -1 : 1)),
        deleted: canonDeleted(merged.collectionTombstones),
      };
      const remoteIndexNorm =
        remoteIndex && remoteIndex.schemaVersion === 2
          ? {
              schemaVersion: 2,
              collections: [...(remoteIndex.collections || [])]
                .map((e) => ({ id: e.id, name: e.name, lastModified: e.lastModified ?? 0 }))
                .sort((a, b) => (a.id < b.id ? -1 : 1)),
              deleted: canonDeleted(remoteIndex.deleted),
            }
          : null;
      // If any collection failed to fetch, do NOT rewrite the index (it could drop
      // an unread remote-only collection) and do NOT advance the watermark — leave
      // the pending flag set so the whole thing is retried.
      if (failedFetch.size === 0) {
        if (JSON.stringify(newIndex) !== JSON.stringify(remoteIndexNorm)) {
          const written = await writeFixed(INDEX_FILE, newIndex);
          store.setLastSyncedAt("flashcards", written.modifiedTime);
        } else {
          store.setLastSyncedAt("flashcards", indexRes.modifiedTime);
        }
      }

      // 7. Persist canonical sync metadata. A change the user made while this sync was
      // in flight is folded in first, as the bookmark and history syncs do (B1): the
      // rebased live state is merged once more against what was just published, and
      // the collections it touched stay dirty so the next sync uploads them.
      const live = resolveLocal(getLocal);
      let result = merged;
      let rebased = null;
      if (flashcardStateChanged(local, live)) {
        rebased = rebaseMidSyncChanges(local, live, merged);
        const published = {};
        for (const [name, cards] of Object.entries(merged.collections)) {
          const id = merged.collectionIdMap[name];
          if (failedFetch.has(id) || rebased.leftOut.has(id)) continue;
          const lm = merged.collectionMeta[id]?.lastModified ?? 0;
          published[id] = buildCollectionPayload(id, name, cards, lm, merged.flashcardTombstones[id] || {});
        }
        result = mergeCollections(
          rebased.collections,
          rebased.idMap,
          rebased.fcTombstones,
          rebased.collectionTombstones,
          newIndex,
          published,
          rebased.collectionMeta,
          mintId
        );
      }
      store.setCollectionIdMap(result.collectionIdMap);
      store.setCollectionMeta(result.collectionMeta);
      store.setFlashcardTombstonesAll(result.flashcardTombstones);
      store.setCollectionTombstones(result.collectionTombstones);
      store.setSyncedCollectionLMMap(newSyncedMT);
      if (rebased) {
        for (const id of Object.values(result.collectionIdMap)) {
          if (rebased.touched.has(id)) store.markCollectionDirty(id);
        }
      }
      if (failedFetch.size === 0 && !rebased) store.clearPendingSyncFlag("flashcards");
      else store.setPendingSyncFlag("flashcards", true);

      return result; // { collections, collectionIdMap, collectionMeta, flashcardTombstones, collectionTombstones }
      } catch (e) {
        store.setPendingSyncFlag("flashcards", true);
        throw e;
      }
    });

  // ── Poll change detection (server-time vs server-time) ──────────────────────
  const detectRemoteChanges = async () => {
    const watermarks = store.getLastSyncedAt();
    const peek = async (name) => {
      const files = await drive.listByName(name).catch(() => []);
      if (!files.length) return { exists: false, modifiedTime: 0, duplicates: false };
      const newest = files.reduce((m, f) => Math.max(m, f.modifiedTime), 0);
      return { exists: true, modifiedTime: newest, duplicates: files.length > 1 };
    };
    const [bm, idx, hist, colFiles] = await Promise.all([
      peek(BOOKMARKS_FILE),
      peek(INDEX_FILE),
      peek(HISTORY_FILE),
      drive.listByPrefix(COLLECTION_PREFIX).catch(() => []),
    ]);
    // Per-collection change detection: compare each collection file's server
    // modifiedTime to its persisted synced value (avoids a perpetual no-op
    // resync loop that a global index-vs-collection watermark comparison causes).
    const syncedMT = store.getSyncedCollectionLM();
    const colNameCounts = new Map();
    let anyCollectionChanged = false;
    for (const f of colFiles) {
      colNameCounts.set(f.name, (colNameCounts.get(f.name) || 0) + 1);
      const id = collectionIdFromName(f.name);
      if (id && f.modifiedTime > (syncedMT[id] ?? -1)) anyCollectionChanged = true;
    }
    const colDup = [...colNameCounts.values()].some((n) => n > 1);

    return {
      bookmarks: bm.duplicates || (bm.exists && bm.modifiedTime > watermarks.bookmarks),
      history: hist.duplicates || (hist.exists && hist.modifiedTime > watermarks.history),
      flashcards:
        idx.duplicates ||
        colDup ||
        anyCollectionChanged ||
        (idx.exists && idx.modifiedTime > watermarks.flashcards),
    };
  };

  return {
    syncBookmarks,
    syncHistory,
    syncFlashcards,
    detectRemoteChanges,
    _resolveFixed: resolveFixed,
    _deleteCollection: deleteCollection,
  };
};
